import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  HostBinding,
  inject,
  Input,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DoughnutChartComponent } from '@components/element-widgets/doughnut-chart/doughnut-chart.component';
import { ElementErrorDirective } from '@components/element-widgets/element-error/element-error.directive';
import { FondDetailsComponent } from '@components/fond-details/fond-details.component';
import { NoSearchResultsComponent } from '@components/no-search-results/no-search-results.component';
import { TranslocoModule } from '@ngneat/transloco';
import { CustomEvents } from '@utils/events/custom-events';
import { EventContext } from '@utils/events/event-context';
import { firstValueFrom } from 'rxjs';
import { ElementContext } from 'src/app/element-handling';
import { ElementContentStringPipe } from 'src/app/element-handling/element-content-string.pipe';
import { ElementPipe } from 'src/app/element-handling/element.pipe';
import { ProvideElementsDirective } from 'src/app/element-handling/provide-elements.directive';
import { ShadowStylesDirective } from 'src/boilerplate/shadow-styles.directive';
import { CUID } from 'src/environments/cuids';
import { DocumentElementResponse } from 'src/openapi-client';

@Component({
  selector: 'uebersicht-gesamtbestand',
  standalone: true,
  imports: [
    AsyncPipe,
    TranslocoModule,

    DoughnutChartComponent,
    FondDetailsComponent,
    NoSearchResultsComponent,

    ElementPipe,
    ElementContentStringPipe,

    ProvideElementsDirective,
    ElementErrorDirective,
  ],
  styleUrls: ['./uebersicht-gesamtbestand.component.scss'],
  templateUrl: './uebersicht-gesamtbestand.component.html',
  encapsulation: ViewEncapsulation.ShadowDom,
  hostDirectives: [ShadowStylesDirective],
  providers: [EventContext],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UebersichtGesamtbestandComponent {
  @Input({ required: false }) @HostBinding('style.width') public width: string = '885px';
  @Input({ required: false }) @HostBinding('style.height') public height: string = '630px';
  @Input({ required: false }) public fondsPortraitUrl: string = '/funds?fund={}';

  @ViewChild('details', { read: ViewContainerRef }) protected detailsViewRef: ViewContainerRef;
  @ViewChild('detailsTemplate', { read: TemplateRef }) protected detailsTemplate: TemplateRef<unknown>;

  protected readonly selectedFund: WritableSignal<string> = signal('');
  protected readonly displayingDetails: WritableSignal<boolean> = signal(false);
  protected readonly displayDetailsHideBackButton: WritableSignal<boolean> = signal(false);
  protected readonly displayUserHasNoFundsMessage: WritableSignal<boolean> = signal(false);
  protected readonly redirectUrl: Signal<string> = computed(() =>
    this.fondsPortraitUrl.replace('{}', this.selectedFund())
  );

  protected readonly CUID = CUID.gesamtbestand;
  protected readonly chartElement: string = 'Data';

  private readonly eventContext: EventContext = inject(EventContext);

  constructor() {
    this.eventContext
      .getEvent(CustomEvents.TreeMapClick)
      .pipe(takeUntilDestroyed())
      .subscribe(({ data }) => this.selectFund(data));

    this.eventContext
      .getEvent(CustomEvents.Back)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.displayingDetails.set(false));

    this.eventContext
      .getEvent(CustomEvents.ElementsLoaded)
      .pipe(takeUntilDestroyed())
      .subscribe(async ({ data }) => {
        if (data.source.documentId !== this.CUID.treemap) {
          return;
        }

        const fundsCount: number = await this.getFundsCount(data.source.elementContext);

        if (fundsCount > 1) {
          this.displayUserHasNoFundsMessage.set(false);
          return;
        }

        if (fundsCount === 0) {
          this.displayUserHasNoFundsMessage.set(true);
          return;
        }

        const funds: string | null = await this.tryGetSingularFunds(data.source.elementContext);

        if (funds === null) {
          this.displayUserHasNoFundsMessage.set(true);
          return;
        }

        this.displayDetailsHideBackButton.set(true);
        this.displayUserHasNoFundsMessage.set(false);
        this.selectFund(funds);
      });
  }

  private selectFund(fund: string): void {
    const fundsSwitch: boolean = fund !== this.selectedFund();

    this.selectedFund.set(fund);
    this.displayingDetails.set(true);

    if (fundsSwitch) {
      this.detailsViewRef.clear();
      this.detailsViewRef.createEmbeddedView(this.detailsTemplate);
    }
  }

  private async getFundsCount(elements: ElementContext): Promise<number> {
    const chart: DocumentElementResponse = await firstValueFrom(elements.getElement(this.chartElement));

    if (!chart || !chart.renderable) {
      return 0;
    }

    const chartOptions: Highcharts.Options = chart.renderable as Highcharts.Options;

    if (!chartOptions.series || chartOptions.series.length < 1) {
      return 0;
    }

    return (chartOptions.series as Array<Highcharts.SeriesPieOptions>).reduce(
      (accumulator, currentSeries) => accumulator + currentSeries.data.length,
      0
    );
  }

  private async tryGetSingularFunds(elements: ElementContext): Promise<string | null> {
    const chart: DocumentElementResponse = await firstValueFrom(elements.getElement(this.chartElement));
    const chartOptions: Highcharts.Options = chart.renderable as Highcharts.Options;

    for (const series of chartOptions.series as Array<Highcharts.SeriesPieOptions>) {
      if (series.data.length < 1) {
        continue;
      }

      const fund: unknown = series.custom?.['key']?.[0] ?? (series.data[0] as Highcharts.PointOptionsObject).name ?? series.data[0];

      if (fund === undefined) {
        continue;
      }

      return `${fund}`;
    }

    return null;
  }
}
